<template>
  <div>
    <nav class="toolbox toolbox-pagination" v-if="showPagination">
      <div class="toolbox-item toolbox-show">
        <label>{{ paginationResultsTitle }}</label>
      </div>
      <ul class="pagination d-none d-md-flex">
        <li
          class="page-item"
          :class="{ disabled: prevDisabled }"
          @click.prevent.stop="goToPage(1)"
        >
          <span class="page-link page-link-btn">
            <i class="icon-angle-double-left" />
          </span>
        </li>
        <li
          class="page-item"
          :class="{ disabled: prevDisabled }"
          @click.prevent.stop="handlePreviousPage"
        >
          <span class="page-link page-link-btn"><i class="icon-angle-left"></i></span>
        </li>
        <li
          class="page-item"
          :class="{ active: index === pager.currentPage }"
          v-for="index in pages"
          :key="index"
          @click.stop.prevent="goToPage(index)"
        >
          <span class="page-link">{{ index }}</span>
        </li>
        <li
          class="page-item"
          :class="{ disabled: nextDisabled }"
          @click.prevent.stop="handleNextPage"
        >
          <span class="page-link page-link-btn"><i class="icon-angle-right"></i></span>
        </li>
        <li
          class="page-item"
          :class="{ disabled: nextDisabled }"
          @click.prevent.stop="goToPage(pager.totalPages)"
        >
          <span class="page-link page-link-btn">
            <i class="icon-angle-double-right" />
          </span>
        </li>
      </ul>
    </nav>
    <div
      v-if="canLoadMore && isMobile"
      ref="sentinel"
      class="infinite-scroll-sentinel"
      aria-hidden="true"
    />
  </div>
</template>

<script>
import { pager, translations } from '@/mixins';

export default {
  name: 'Pagination',
  mixins: [pager, translations],
  data() {
    return {
      actionName: this.action,
      intersectionObserver: null,
      isLoadingMore: false,
      isMobile: false,
      mediaQuery: null,
    };
  },
  computed: {
    canLoadMore() {
      return this.showPagination && !this.nextDisabled;
    },
  },
  props: {
    action: String,
    paginationProps: {
      type: Object,
      default: () => ({}),
    },
  },
  mounted() {
    if (typeof window !== 'undefined' && window.matchMedia) {
      this.mediaQuery = window.matchMedia('(max-width: 767px)');
      this.isMobile = this.mediaQuery.matches;
      this.mediaQuery.addEventListener('change', this.handleMediaQueryChange);
    }

    this.fetchPage(1).finally(() => {
      this.$nextTick(() => {
        if (this.isMobile) {
          this.setupIntersectionObserver();
        }
      });
    });
  },
  beforeDestroy() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleMediaQueryChange);
    }
    this.teardownIntersectionObserver();
  },
  methods: {
    handleMediaQueryChange(e) {
      this.isMobile = e.matches;
      if (!this.isMobile) {
        this.teardownIntersectionObserver();
      } else if (this.canLoadMore) {
        this.$nextTick(() => {
          this.setupIntersectionObserver();
        });
      }
    },
    buildRequestPayload(page, appendResults = false) {
      return {
        ...this.searchConfig,
        ...this.paginationProps,
        page,
        appendResults,
      };
    },
    fetchPage(page, appendResults = false) {
      return this.$store.dispatch(this.actionName, this.buildRequestPayload(page, appendResults));
    },
    setupIntersectionObserver() {
      if (typeof window === 'undefined' || !window.IntersectionObserver) {
        return;
      }

      this.teardownIntersectionObserver();

      this.intersectionObserver = new window.IntersectionObserver(
        (entries) => {
          const sentinelIsVisible = entries.some((entry) => entry.isIntersecting);

          if (sentinelIsVisible) {
            this.loadNextPage();
          }
        },
        {
          rootMargin: '200px 0px',
        },
      );

      this.observeSentinel();
    },
    teardownIntersectionObserver() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
        this.intersectionObserver = null;
      }
    },
    observeSentinel() {
      if (this.intersectionObserver && this.$refs.sentinel && this.canLoadMore) {
        this.intersectionObserver.observe(this.$refs.sentinel);
      }
    },
    loadNextPage() {
      if (!this.canLoadMore || this.isLoadingMore) {
        return Promise.resolve();
      }

      this.isLoadingMore = true;
      this.teardownIntersectionObserver();

      return this.fetchPage(this.pager.currentPage + 1, true).finally(() => {
        this.isLoadingMore = false;

        if (this.canLoadMore) {
          this.$nextTick(() => {
            this.setupIntersectionObserver();
          });
        }
      });
    },
  },
  watch: {
    canLoadMore(value) {
      if (!this.isMobile) return;

      if (!value) {
        this.teardownIntersectionObserver();
        return;
      }

      this.$nextTick(() => {
        this.setupIntersectionObserver();
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.toolbox.toolbox-pagination {
  align-items: center;
  row-gap: 1rem;
}

.infinite-scroll-sentinel {
  height: 1px;
  width: 100%;
}

.page-item:not(.disabled) {
  cursor: pointer;
}

.page-item.disabled {
  cursor: not-allowed;
}

.page-link.page-link-btn {
  border: 0.1rem solid #e9e9e9;
  min-width: 3.2rem;
  font-size: 1.4rem;
}

@media (max-width: 991px) {
  .toolbox.toolbox-pagination {
    align-items: stretch;
    justify-content: center;
    margin-bottom: 3rem;
    padding-top: 1.4rem;
  }
}
</style>
